import React, { useEffect, useState } from "react";
import {
  getActiveLotteryCard,
  listenToCard,
  listenToWinners,
  listenToUserHistory,
  buyNumbers,
  listenToCountdown,
  closeAndCreateNewCard,
  listenToRecentDrawn,
  listenToPendingCards,
  drawWinner,
  getCardDetails,
} from "./utils";
import LotteryCard from "./LotteryCard";
import BuyModal from "./BuyModal";
import WinnerModal from "./WinnerModal";
import UserHistory from "./UserHistory";
import RecentWinners from "./RecentWinners";
import CountdownTimer from "./CountdownTimer";
import PendingCardDetailsModal from "./PendingCardDetailsModal";

function PendingPanel({ cards, onOpen }) {
  if (!cards?.length) return null;
  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <div className="font-bold mb-2">Pending Draw</div>
      <ul className="space-y-1 text-sm">
        {cards.map((c) => {
          const t = c.batchDrawAt?.toDate?.() || new Date(c.batchDrawAt);
          return (
            <li
              key={c.id}
              className="flex justify-between cursor-pointer hover:text-white"
              onClick={() => onOpen(c.id)}
            >
              <span>Card #{c.id.slice(0, 8)}…</span>
              <span className="text-yellow-400">
                draw @ {t.toLocaleTimeString()}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RecentDrawsPanel({ cards }) {
  if (!cards?.length) return null;
  return (
    <div className="bg-slate-800 rounded-xl p-4">
      <div className="font-bold mb-2">Recent Draws</div>
      <ul className="space-y-1 text-sm">
        {cards.map((c) => (
          <li key={c.id} className="flex justify-between items-center">
            <div>
              <span className="rounded-full px-2 py-0.5 bg-yellow-300 text-black font-bold mr-2">
                {c.winningNumber}
              </span>
              <span>Card #{c.id.slice(0, 8)}…</span>
            </div>
            <div
              className={
                c.winner ? "text-green-400 font-bold" : "text-gray-400"
              }
            >
              {c.winner
                ? `${c.winner.username} $${c.winner.amount}`
                : "No winner"}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function LotteryPage({ user, balance }) {
  const [card, setCard] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showBuy, setShowBuy] = useState(false);
  const [showWinner, setShowWinner] = useState(false);
  const [winnerData, setWinnerData] = useState(null);
  const [recentWinners, setRecentWinners] = useState([]);
  const [history, setHistory] = useState([]);
  const [countdown, setCountdown] = useState(0);
  const [quickPickCount, setQuickPickCount] = useState(1);
  const [pendingCards, setPendingCards] = useState([]);
  const [recentDraws, setRecentDraws] = useState([]);
  const [cardDetailsOpen, setCardDetailsOpen] = useState(false);
  const [cardDetailsData, setCardDetailsData] = useState(null);

  useEffect(() => {
    if (card) console.log("ACTIVE CARD batchDrawAt =", card.batchDrawAt);
  }, [card]);

  // Fetch active card, listen for updates
  useEffect(() => {
    getActiveLotteryCard().then(setCard);
    const unsub = listenToCard(setCard);
    const unsubWinners = listenToWinners(setRecentWinners);
    const unsubHistory = listenToUserHistory(user?.uid, setHistory);
    const unsubCountdown = listenToCountdown(setCountdown);
    const unsubPending = listenToPendingCards(setPendingCards);
    const unsubDrawn = listenToRecentDrawn(setRecentDraws);
    return () => {
      unsub();
      unsubWinners();
      unsubHistory();
      unsubCountdown();
      unsubPending();
      unsubDrawn();
    };
  }, [user]);

  useEffect(() => {
    if (card && card.filled.length === 100 && card.status === "active") {
      // Mark the card as closed and create a new card
      closeAndCreateNewCard(card.id);
    }
  }, [card]);

  // Winner modal logic
  useEffect(() => {
    if (card?.winner) {
      setWinnerData(card.winner);
      setShowWinner(true);
      setTimeout(() => setShowWinner(false), 7000); // auto-close modal
    }
  }, [card?.winner]);

  // Reset selection when card changes
  useEffect(() => setSelected([]), [card?.id]);

  // const handleNumberClick = (num) => {
  //   if (!card || card.filled.includes(num)) return;
  //   // Allow multi select, add/remove toggle
  //   setSelected((prev) =>
  //     prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num]
  //   );
  // };

  // replace handleNumberClick with this version
  const handleNumberClick = (num) => {
    if (!card) return;
    const filled = card.filled || [];
    const locked = card.locked || [];
    if (filled.includes(num) || locked.includes(num)) return;

    setSelected((prev) =>
      prev.includes(num) ? prev.filter((n) => n !== num) : [...prev, num]
    );
  };

  const handleBuy = async () => {
    setShowBuy(true);
  };

  // const handleConfirmBuy = async (numbers) => {
  //   await buyNumbers(card.id, user.uid, numbers); // update with payment logic
  //   setSelected([]);
  //   setShowBuy(false);
  // };

  const handleConfirmBuy = async (numbers) => {
    try {
      const res = await buyNumbers(card.id, user.uid, numbers);
      setSelected([]);
      setShowBuy(false);

      if (res.rejected.length) {
        // show a toast or inline note
        alert(
          `Some numbers were already taken: ${res.rejected.join(", ")}.\n` +
            `Purchased ${
              res.bought.length
            } number(s). Charged $${res.charged.toFixed(2)}.`
        );
      } else {
        // all good
        // toast.success(`Bought ${res.bought.length} number(s) for $${res.charged.toFixed(2)}`);
      }
    } catch (e) {
      if (e.message === "InsufficientFunds") {
        alert("Not enough balance.");
      } else if (e.message === "AllSelectedNumbersTaken") {
        alert("Those numbers were just taken. Please pick others.");
      } else {
        console.error(e);
        alert("Purchase failed. Please try again.");
      }
    }
  };

  // console.log("countdown", countdown);

  const openCardDetails = async (cardId) => {
    const data = await getCardDetails(cardId);
    setCardDetailsData(data);
    setCardDetailsOpen(true);
  };

  return (
    <div className="max-w-3xl mx-auto mt-8 md:mt-24">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-gradient">🎲 Daily Lottery</h1>
        <CountdownTimer target={card?.batchDrawAt} />
      </div>
      {card && (
        <>
          <LotteryCard
            card={card}
            selected={selected}
            onNumberClick={handleNumberClick}
          />
          <div className="flex justify-between items-center mt-4">
            <div>
              <button
                className="bg-gradient-to-r from-blue-500 to-green-400 text-white px-4 py-2 rounded-2xl shadow"
                disabled={selected.length === 0}
                onClick={handleBuy}
              >
                Buy {selected.length} Number{selected.length > 1 ? "s" : ""}
                {selected.length > 0 &&
                  ` ($${(selected.length * 0.5).toFixed(2)})`}
              </button>
            </div>
            <button
              className="bg-slate-700 text-white px-3 py-1 rounded-xl"
              onClick={() => setSelected([])}
              disabled={selected.length === 0}
            >
              Clear
            </button>
            <div className="flex items-center gap-2">
              <select
                className="rounded bg-slate-700 text-white px-2 py-1 ml-2"
                value={quickPickCount}
                onChange={(e) => setQuickPickCount(Number(e.target.value))}
              >
                {[...Array(20).keys()].map((i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
              <button
                className="bg-yellow-400 hover:bg-yellow-500 text-black px-3 py-1 rounded-xl"
                onClick={() => {
                  const avail = [...Array(100).keys()].filter(
                    (num) => !card.filled.includes(num)
                  );
                  if (avail.length === 0) return;
                  let pickCount = Math.min(quickPickCount, avail.length);
                  let nums = [];
                  while (nums.length < pickCount) {
                    const r = avail[Math.floor(Math.random() * avail.length)];
                    if (!nums.includes(r)) nums.push(r);
                  }
                  setSelected(nums);
                }}
                disabled={card?.filled.length === 100}
              >
                Quick Pick
              </button>
            </div>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <RecentWinners winners={recentWinners} />
            <UserHistory history={history} />
          </div>
        </>
      )}

      <PendingCardDetailsModal
        open={cardDetailsOpen}
        onClose={() => setCardDetailsOpen(false)}
        data={cardDetailsData}
      />

      {showBuy && (
        <BuyModal
          numbers={selected}
          price={selected.length * 0.5}
          onClose={() => setShowBuy(false)}
          onConfirm={handleConfirmBuy}
          balance={balance}
        />
      )}

      {showWinner && winnerData && (
        <WinnerModal winner={winnerData} onClose={() => setShowWinner(false)} />
      )}

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <PendingPanel cards={pendingCards} onOpen={openCardDetails} />
        <RecentDrawsPanel cards={recentDraws} />
      </div>
    </div>
  );
}
